import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  getGenieData,
  predictResponse,
  generateMessages,
  replyGenie,
  resetSession,
  generateAlternate,
  getGenieBrandOptions,
  evaluateAlternateMessages,
  evaluateGeneratedMessages,
  createCustomMessages,
  getGenieAudiences,
} from './genieActions';
import { ASSISTANTS_CATEGORY, GENIE_EVALUATE_TYPE } from '../../constants';
import {
  evaluateAlternateMessagesConsumer,
  evaluateGeneratedMessagesConsumer,
  generateConsumerAlternate,
  generateConsumerGenieMessages,
  getConsumerGenieData,
  predictConsumerResponse,
  replyConsumerGenie,
  resetGenieSession,
  getConsumerGenieAudiences,
} from '../consumer/genie/consumerGenieActions';

const genieInitialState = {
  brand: '',
  projectName: '',
  projectId: '',
  threadId: '',

  isPredicting: false,
  isGeneratingAlternate: false,
  predictedInitMessage: null,
  predictedMessages: [],

  channels: {
    [ASSISTANTS_CATEGORY.GENERATE_ALL]: {
      hint: '',
    },
  },

  assistants: {
    [ASSISTANTS_CATEGORY.GENERATE_ALL]: [],
  },

  isFormLoading: false,
  isGenerating: false,
  isResetting: false,
  messages: [],

  brandOptions: {},

  audiences: {},
  isAudiencesLoading: false,

  generationForm: {
    url: '',
    byPass: '',
    focusOn: '',
  },

  evaluateActions: {
    [GENIE_EVALUATE_TYPE.ALTERNATE]: {
      pending: false,
      abortController: null,
    },
    [GENIE_EVALUATE_TYPE.GENERATED]: {
      pending: false,
      abortController: null,
    },
  },
};

const genieSlice = createSlice({
  name: 'genie',
  initialState: genieInitialState,
  reducers: {
    updateEvaluatedPredictedMessages: (state, action) => {
      const messagesWithPrediction = action.payload;
      const updatedMessages = [...state.predictedMessages].map((existedMessage) => {
        const updatedMessage = messagesWithPrediction.find(({ message }) => message === existedMessage.message);
        return updatedMessage ? { ...existedMessage, ...updatedMessage } : existedMessage;
      });
      state.predictedMessages = updatedMessages;
    },
    updateEvaluatedGeneratedMessages: (state, action) => {
      const messagesWithPrediction = action.payload;
      const updatedMessages = [...state.messages].map((existedMessage) => {
        const updatedMessage = messagesWithPrediction.find(({ message }) => message === existedMessage.messageText);
        return updatedMessage ? { ...existedMessage, ...updatedMessage } : existedMessage;
      });
      state.messages = updatedMessages;
    },

    setEvalAbortController: (state, action) => {
      state.evaluateActions[action.payload.type].abortController = action.payload.controller;
    },
    cancelEvaluationByType: (state, action) => {
      const evaluationType = action.payload;

      if (state.evaluateActions[evaluationType].abortController) {
        state.evaluateActions[evaluationType].abortController.abort();
        state.evaluateActions[evaluationType].abortController = null;
      }

      state.evaluateActions[evaluationType].pending = false;
    },

    changeGenerationForm: (state, action) => {
      state.generationForm = { ...state.generationForm, ...action.payload };
    },

    resetGenieParams: () => genieInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGenieData.fulfilled, (state, action) => {
        const { assistants, brand, projectName, projectId } = action.payload;
        state.assistants = assistants;
        state.brand = brand;
        state.projectName = projectName;
        state.projectId = projectId;
      })

      .addCase(getGenieBrandOptions.fulfilled, (state, action) => {
        state.brandOptions = action.payload;
      })

      .addMatcher(isAnyOf(getGenieAudiences.fulfilled, getConsumerGenieAudiences.fulfilled), (state, action) => {
        state.audiences = action.payload;
        state.isAudiencesLoading = false;
      })
      .addMatcher(isAnyOf(getGenieAudiences.pending, getConsumerGenieAudiences.pending), (state) => {
        state.isAudiencesLoading = true;
      })
      .addMatcher(isAnyOf(getGenieAudiences.rejected, getConsumerGenieAudiences.rejected), (state) => {
        state.isAudiencesLoading = false;
      })

      .addMatcher(isAnyOf(evaluateGeneratedMessages.pending, evaluateGeneratedMessagesConsumer.pending), (state) => {
        state.evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].pending = true;
      })
      .addMatcher(
        isAnyOf(evaluateGeneratedMessages.fulfilled, evaluateGeneratedMessagesConsumer.fulfilled),
        (state) => {
          state.evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].pending = false;
          state.evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].abortController = null;
        },
      )
      .addMatcher(isAnyOf(evaluateGeneratedMessages.rejected, evaluateGeneratedMessagesConsumer.rejected), (state) => {
        state.evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].pending = false;
        state.evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].abortController = null;
      })

      .addMatcher(isAnyOf(evaluateAlternateMessages.pending, evaluateAlternateMessagesConsumer.pending), (state) => {
        state.evaluateActions[GENIE_EVALUATE_TYPE.ALTERNATE].pending = true;
      })
      .addMatcher(
        isAnyOf(evaluateAlternateMessages.fulfilled, evaluateAlternateMessagesConsumer.fulfilled),
        (state) => {
          state.evaluateActions[GENIE_EVALUATE_TYPE.ALTERNATE].pending = false;
          state.evaluateActions[GENIE_EVALUATE_TYPE.ALTERNATE].abortController = null;
        },
      )
      .addMatcher(isAnyOf(evaluateAlternateMessages.rejected, evaluateAlternateMessagesConsumer.rejected), (state) => {
        state.evaluateActions[GENIE_EVALUATE_TYPE.ALTERNATE].pending = false;
        state.evaluateActions[GENIE_EVALUATE_TYPE.ALTERNATE].abortController = null;
      })

      .addMatcher(isAnyOf(getConsumerGenieData.fulfilled), (state, action) => {
        const { assistants, brand, projectName } = action.payload;
        state.assistants = assistants;
        state.brand = brand;
        state.projectName = projectName;
      })

      .addMatcher(isAnyOf(resetSession.fulfilled, resetGenieSession.fulfilled), (state) => {
        state.isResetting = false;
        state.messages = [];
        state.channels = genieInitialState.channels;
      })
      .addMatcher(isAnyOf(resetSession.pending, resetGenieSession.pending), (state) => {
        state.isResetting = true;
      })
      .addMatcher(isAnyOf(resetSession.rejected, resetGenieSession.rejected), (state) => {
        state.isResetting = false;
      })

      .addMatcher(isAnyOf(createCustomMessages.fulfilled), (state, action) => {
        const messages = action.payload;

        const formattedNewMessages = messages.map((message, index) => ({
          messageText: message,
          count: state.messages.length + messages.length - index,
          isNew: true,
          isManual: true,
        }));

        const formattedOldMessages = state.messages.map(
          ({ messageText, count, interest, action, uncertainty, error }) => ({
            messageText,
            count,
            isNew: false,
            interest,
            action,
            uncertainty,
            error,
          }),
        );

        state.messages = [...formattedNewMessages, ...formattedOldMessages];
      })

      .addMatcher(isAnyOf(replyGenie.fulfilled, replyConsumerGenie.fulfilled), (state, action) => {
        const { messages, threadId, hint } = action.payload;

        const formattedNewMessages = messages.map((message, index) => ({
          messageText: message,
          count: state.messages.length + messages.length - index,
          isNew: true,
        }));

        const formattedOldMessages = state.messages.map(
          ({ messageText, count, interest, action, uncertainty, isManual }) => ({
            messageText,
            count,
            isNew: false,
            interest,
            action,
            uncertainty,
            isManual,
          }),
        );

        state.messages = [...formattedNewMessages, ...formattedOldMessages];
        state.threadId = threadId;
        state.channels[ASSISTANTS_CATEGORY.GENERATE_ALL].hint = hint;
      })

      .addMatcher(isAnyOf(generateMessages.fulfilled, generateConsumerGenieMessages.fulfilled), (state, action) => {
        const { messages, threadId, hint } = action.payload;

        const formattedNewMessages = messages.map((message, index) => ({
          messageText: message,
          count: messages.length - index,
          isNew: true,
        }));

        state.messages = [...formattedNewMessages];
        state.threadId = threadId;
        state.channels[ASSISTANTS_CATEGORY.GENERATE_ALL].hint = hint;

        state.isGenerating = false;
      })
      .addMatcher(isAnyOf(generateMessages.pending, generateConsumerGenieMessages.pending), (state) => {
        state.isGenerating = true;
      })
      .addMatcher(isAnyOf(generateMessages.rejected, generateConsumerGenieMessages.rejected), (state) => {
        state.isGenerating = false;
      })

      .addMatcher(isAnyOf(predictResponse.fulfilled, predictConsumerResponse.fulfilled), (state, action) => {
        state.isPredicting = false;
        state.predictedInitMessage = action.payload;
      })
      .addMatcher(isAnyOf(predictResponse.pending, predictConsumerResponse.pending), (state) => {
        state.isPredicting = true;
      })
      .addMatcher(isAnyOf(predictResponse.rejected, predictConsumerResponse.rejected), (state) => {
        state.isPredicting = false;
      })

      .addMatcher(isAnyOf(generateAlternate.fulfilled, generateConsumerAlternate.fulfilled), (state, action) => {
        state.isGeneratingAlternate = false;

        const newPredictedMessages = action.payload ?? [];

        state.predictedMessages = newPredictedMessages.map((message, index) => ({
          message: message,
          count: newPredictedMessages.length - index,
        }));
      })
      .addMatcher(isAnyOf(generateAlternate.pending, generateConsumerAlternate.pending), (state) => {
        state.isGeneratingAlternate = true;
      })
      .addMatcher(isAnyOf(generateAlternate.rejected, generateConsumerAlternate.rejected), (state) => {
        state.isGeneratingAlternate = false;
      })

      .addMatcher(
        isAnyOf(
          getGenieData.fulfilled,
          getConsumerGenieData.fulfilled,
          generateMessages.fulfilled,
          generateConsumerGenieMessages.fulfilled,
          replyGenie.fulfilled,
          replyConsumerGenie.fulfilled,
          resetSession.fulfilled,
          resetGenieSession.fulfilled,
        ),
        (state) => {
          state.isFormLoading = false;
        },
      )
      .addMatcher(
        isAnyOf(
          getGenieData.pending,
          getConsumerGenieData.pending,
          generateMessages.pending,
          generateConsumerGenieMessages.pending,
          replyGenie.pending,
          replyConsumerGenie.pending,
          resetSession.pending,
          resetGenieSession.pending,
        ),
        (state) => {
          state.isFormLoading = true;
        },
      )
      .addMatcher(
        isAnyOf(
          getGenieData.rejected,
          getConsumerGenieData.rejected,
          generateMessages.rejected,
          generateConsumerGenieMessages.rejected,
          replyGenie.rejected,
          replyConsumerGenie.rejected,
          resetSession.rejected,
          resetGenieSession.rejected,
        ),
        (state) => {
          state.isFormLoading = false;
        },
      );
  },
});

export const {
  resetGenieParams,

  updateEvaluatedPredictedMessages,
  updateEvaluatedGeneratedMessages,
  setEvalAbortController,
  cancelEvaluationByType,

  changeGenerationForm,
} = genieSlice.actions;

export default genieSlice.reducer;
